// src/db.ts
import Dexie, { type Table } from "dexie";

// ---------- Types ----------
export interface Address {
  id: number;                       // Also PID?!
  udprn: number;                     // Primary ID
  prefix: string;                    // BD / HG / LS / WF / YO
  toDisplay?: string;
  to_display?: string;
  thoroughfare: string | null;
  postcode_normalised: string;
  sort_number: number;
  sort_secondary: string | null;
  post_town?: string | null;
  dependent_locality?: string | null;
}

export interface AddressState {
  udprn: number;                     // PK
  prefix?: string;                   // BD / HG / LS / WF / YO (indexed for offline)
  status: string | null;
  last_note_id: number | null;
  // add any extra fields later
}

export interface Customer {
  id: number;                        // PK (server ID)
  udprn: number;                     // FK
  prefix?: string;                   // BD / HG / LS / WF / YO (indexed for offline)
  name: string | null;
  phone: string | null;
  email: string | null;
  // any other fields you capture
}

export interface NoteSynced {
  id: number;                        // Server note ID
  udprn: number;
  prefix?: string;                   // BD / HG / LS / WF / YO (indexed for offline)
  status: string;
  status_display: string;
  body: string | null;
  created_at: string;                // ISO timestamp
  username: string | null;
}

export type NotePayloadBody = {
  status?: string;
  status_display?: string;
  source_type?: string;
  note?: string;
  client_created_at?: string;
  local_id?: string;
  username?: string | null;
  [key: string]: unknown;
};

export interface NoteQueued {
  id?: number;
  local_id: string;                  // UUID client-only
  udprn: number;
  payload: {
    endpoint?: string;
    body?: NotePayloadBody;
    [key: string]: unknown;
  };
  created_at_client: string;
  retry_count: number;
  last_attempt_at?: string | null;
  error?: string | null;
  queue_status: "pending" | "failed";
}

export interface MetaKV {
  key: string;
  value: unknown;
}

export interface SyncLog {
  id?: number;
  local_id: string;
  udprn: number;
  event_type: "attempt" | "success" | "failed_permanent";
  timestamp: string;
  retry_count: number;
  http_status?: number | null;
  error_message?: string | null;
}

// ---------- Dexie DB ----------
export class CRMOfflineDB extends Dexie {
  addresses!: Table<Address, number>;
  addressStates!: Table<AddressState, number>;
  customers!: Table<Customer, number>;
  notesSynced!: Table<NoteSynced, number>;
  notesQueue!: Table<NoteQueued, string>;
  syncLogs!: Table<SyncLog, number>;
  meta!: Table<MetaKV, string>;

  constructor() {
    super("CRMOfflineDB");

    // Single schema (fresh DBs only). If schema changes, clear Dexie DB.
    this.version(1).stores({
      addresses: `
        udprn,
        prefix,
        thoroughfare,
        [thoroughfare+postcode_normalised]
      `,
      addressStates: `
        udprn,
        prefix
      `,
      customers: `
        udprn,
        prefix
      `,
      notesSynced: `
        &id,
        udprn,
        prefix
      `,
      notesQueue: `
        local_id,
        udprn,
        queue_status,
        last_attempt_at
      `,
      syncLogs: `
        ++id,
        local_id,
        udprn,
        timestamp,
        event_type
      `,
      meta: `
        key
      `,
    });

    // optional hooks here
  }
}

export const db = new CRMOfflineDB();
